/**
 * The single mutation surface of the application.
 *
 * Components never call `store.setState` directly and never import the
 * scheduler or storage layers - they call an action. That keeps every state
 * transition in one readable file and makes the components trivially testable
 * with a stub `actions` object.
 */

import {
  DAY_KEYS,
  addDays,
  classifyShiftCell,
  mondayOf,
} from '../core/time.js';
import { createRota, createStaffRule, normaliseRota } from '../core/model.js';
import { createDefaultSettings, normaliseSettings } from '../core/settings.js';
import { uid } from '../core/ids.js';
import {
  buildDemoRota,
  buildHistoricalRota,
  DEMO_STAFF_RULES,
  DEMO_WEEK_COMMENCING,
  HISTORICAL_ROTA_ROWS,
} from '../data/demoRota.js';
import { buildSeedAliasBook, buildSeedStaffRules } from '../data/defaults.js';
import {
  acknowledgeWarning as acknowledgeWarningInSchedule,
  applyManualAssignment,
  generateSchedule,
  removeAssignment as removeAssignmentFromSchedule,
  scheduleViolatesCurrentHardRules,
  toggleLock as toggleLockInSchedule,
} from '../scheduler/index.js';
import { createDefaultAppState, resetAppState } from '../io/storage.js';

/**
 * @param {ReturnType<import('./store.js').createStore>} store
 */
export function createActions(store) {
  /** Shorthand for a runtime-only change (never triggers a save). */
  const setRuntime = (patch) => {
    store.setState(
      (state) => ({ runtime: { ...state.runtime, ...(typeof patch === 'function' ? patch(state.runtime) : patch) } }),
      { reason: 'runtime' },
    );
  };

  /** Dependencies the scheduler facade needs, gathered from current state. */
  const schedulerDeps = (state) => {
    const rota = state.rotas?.[state.weekCommencing] ?? null;
    return {
      rota,
      settings: state.settings,
      staffRules: rota?.source === 'demo'
        ? [...state.staffRules, ...DEMO_STAFF_RULES]
        : state.staffRules,
      aliasBook: state.aliasBook,
    };
  };

  /** Apply a pure transform to the current week's rota. */
  function updateRota(transform) {
    store.setState(
      (state) => {
        const week = state.weekCommencing;
        const existing = state.rotas?.[week] ?? createRota(week, []);
        const next = normaliseRota(transform(existing));
        return { rotas: { ...state.rotas, [week]: next } };
      },
      { reason: 'rota' },
    );
  }

  /** Apply a pure transform to the current week's schedule. */
  function updateSchedule(transform) {
    const state = store.getState();
    const schedule = state.schedules?.[state.weekCommencing];
    if (!schedule) {
      actions.toast({ message: 'Generate a schedule first.', tone: 'warn' });
      return;
    }
    try {
      const next = transform(schedule, state);
      store.setState(
        (current) => ({ schedules: { ...current.schedules, [current.weekCommencing]: next } }),
        { reason: 'schedule' },
      );
    } catch (error) {
      console.error('[lunchly] schedule update failed', error);
      actions.toast({ message: `Could not update the schedule: ${error.message}`, tone: 'danger' });
    }
  }

  const actions = {
    /* ---------------------------------------------------------------- */
    /* Toasts                                                            */
    /* ---------------------------------------------------------------- */

    toast({ message, tone = 'info', duration = 5000 }) {
      const entry = { id: uid('toast'), message, tone, duration };
      setRuntime((runtime) => ({ toasts: [...(runtime.toasts ?? []), entry] }));
      return entry.id;
    },

    dismissToast(id) {
      setRuntime((runtime) => ({ toasts: (runtime.toasts ?? []).filter((item) => item.id !== id) }));
    },

    /* ---------------------------------------------------------------- */
    /* Navigation & chrome                                               */
    /* ---------------------------------------------------------------- */

    setWeek(weekCommencing) {
      const monday = mondayOf(weekCommencing);
      if (!monday) return;
      store.setState({ weekCommencing: monday }, { reason: 'week' });
    },

    shiftWeek(deltaWeeks) {
      const current = store.getState().weekCommencing;
      actions.setWeek(addDays(current, deltaWeeks * 7));
    },

    setView(view) {
      store.setState((state) => ({ ui: { ...state.ui, view } }), { reason: 'view' });
    },

    setActiveDay(dayKey) {
      store.setState((state) => ({ ui: { ...state.ui, activeDay: dayKey } }), { reason: 'view' });
    },

    setTheme(theme) {
      const resolved = theme === 'system'
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : theme;
      document.documentElement.dataset.theme = resolved;
      store.setState({ theme }, { reason: 'theme' });
    },

    openDialog(dialog, payload = null) {
      setRuntime({ dialog, dialogPayload: payload });
    },

    closeDialog() {
      setRuntime({ dialog: null, dialogPayload: null });
    },

    /* ---------------------------------------------------------------- */
    /* Rota editing                                                      */
    /* ---------------------------------------------------------------- */

    /** Replace the whole rota for the current week. */
    setRota(rota, { toastMessage } = {}) {
      const state = store.getState();
      const week = rota.weekCommencing || state.weekCommencing;
      store.setState(
        (current) => ({
          weekCommencing: week,
          rotas: { ...current.rotas, [week]: normaliseRota({ ...rota, weekCommencing: week }) },
        }),
        { reason: 'rota' },
      );
      if (toastMessage) actions.toast({ message: toastMessage, tone: 'ok' });
    },

    /**
     * Replace everything with one freshly imported rota.
     *
     * Every previously imported rota and generated plan is discarded, not merged.
     * Uploading a new rota means the old one is finished with, and leaving it in
     * the store risks the results below showing a plan for a week the manager is
     * no longer looking at.
     */
    replaceRota(rota) {
      const week = mondayOf(rota.weekCommencing) || rota.weekCommencing;
      store.setState(
        () => ({
          weekCommencing: week,
          rotas: { [week]: normaliseRota({ ...rota, weekCommencing: week }) },
          schedules: {},
        }),
        { reason: 'rota' },
      );
    },

    /** Ensure an (empty) rota exists for the current week and return it. */
    ensureRota() {
      const state = store.getState();
      const existing = state.rotas?.[state.weekCommencing];
      if (existing) return existing;
      const blank = createRota(state.weekCommencing, [{ name: '', cells: {} }]);
      store.setState(
        (current) => ({ rotas: { ...current.rotas, [current.weekCommencing]: blank } }),
        { reason: 'rota' },
      );
      return blank;
    },

    updateRotaRowName(rowIndex, name) {
      updateRota((rota) => {
        const rows = rota.rows.map((row, index) => (index === rowIndex ? { ...row, name } : row));
        return { ...rota, rows };
      });
    },

    updateRotaCell(rowIndex, dayKey, value) {
      updateRota((rota) => {
        const rows = rota.rows.map((row, index) => (
          index === rowIndex ? { ...row, cells: { ...row.cells, [dayKey]: value } } : row
        ));
        return { ...rota, rows };
      });
    },

    addRotaRow(name = '') {
      updateRota((rota) => ({
        ...rota,
        rows: [...rota.rows, { employeeId: '', name, cells: emptyCells() }],
      }));
    },

    removeRotaRow(rowIndex) {
      updateRota((rota) => ({
        ...rota,
        rows: rota.rows.filter((_row, index) => index !== rowIndex),
      }));
    },

    clearRotaDay(dayKey) {
      updateRota((rota) => ({
        ...rota,
        rows: rota.rows.map((row) => ({ ...row, cells: { ...row.cells, [dayKey]: '' } })),
      }));
    },

    /* ---------------------------------------------------------------- */
    /* Demo data                                                         */
    /* ---------------------------------------------------------------- */

    loadDemoWeek() {
      const demo = buildDemoRota();
      store.setState(
        (state) => ({
          weekCommencing: DEMO_WEEK_COMMENCING,
          rotas: { ...state.rotas, [DEMO_WEEK_COMMENCING]: normaliseRota(demo) },
        }),
        { reason: 'rota' },
      );
      actions.closeDialog();
      actions.toast({
        message: 'Demo week loaded - 32 staff for w/c 27 July 2026. Press Generate lunches.',
        tone: 'ok',
        duration: 7000,
      });
    },

    clearDemoData() {
      store.setState(
        (state) => {
          const rotas = { ...state.rotas };
          const schedules = { ...state.schedules };
          delete rotas[DEMO_WEEK_COMMENCING];
          delete schedules[DEMO_WEEK_COMMENCING];
          const nextWeek = state.weekCommencing === DEMO_WEEK_COMMENCING
            ? mondayOf(new Date().toISOString().slice(0, 10))
            : state.weekCommencing;
          return { rotas, schedules, weekCommencing: nextWeek };
        },
        { reason: 'rota' },
      );
      actions.toast({ message: 'Demo week removed.', tone: 'info' });
    },

    /* ---------------------------------------------------------------- */
    /* Scheduling                                                        */
    /* ---------------------------------------------------------------- */

    generate({ days = null } = {}) {
      const state = store.getState();
      const deps = schedulerDeps(state);
      if (!deps.rota?.rows?.some((row) => row.name?.trim())) {
        actions.toast({ message: 'Add or import a rota before generating lunches.', tone: 'warn' });
        return;
      }

      setRuntime({ generating: true });
      try {
        const schedule = generateSchedule({
          ...deps,
          previousSchedule: state.schedules?.[state.weekCommencing] ?? null,
          options: days ? { days } : {},
        });
        store.setState(
          (current) => ({
            schedules: { ...current.schedules, [current.weekCommencing]: schedule },
          }),
          { reason: 'generate' },
        );
        const total = DAY_KEYS.reduce(
          (sum, dayKey) => sum + (schedule.days[dayKey]?.assignments?.length ?? 0),
          0,
        );
        const unsolved = schedule.stats?.daysUnsolved ?? 0;
        actions.toast({
          message: days
            ? `Regenerated ${days.length} day${days.length === 1 ? '' : 's'}.`
            : `Generated ${total} lunch${total === 1 ? '' : 'es'} across the week.`
              + (unsolved ? ` ${unsolved} day${unsolved === 1 ? '' : 's'} could not be fully solved.` : ''),
          tone: unsolved ? 'warn' : 'ok',
        });
      } catch (error) {
        console.error('[lunchly] generation failed', error);
        actions.toast({ message: `Could not generate lunches: ${error.message}`, tone: 'danger', duration: 9000 });
      } finally {
        setRuntime({ generating: false });
      }
    },

    /** Replace a cached current-week schedule that predates current rules. */
    refreshScheduleIfNeeded() {
      const state = store.getState();
      const schedule = state.schedules?.[state.weekCommencing] ?? null;
      const deps = schedulerDeps(state);
      if (!schedule || !deps.rota || !scheduleViolatesCurrentHardRules(schedule, deps)) return false;
      // Never throw away a fuller saved plan to regenerate from a damaged PDF
      // import. The manager can still review/re-upload that rota, but startup is
      // strictly non-destructive until every populated cell is understandable.
      if (unreadableRotaCellCount(deps.rota) > 0) return false;

      store.setState(
        (current) => {
          const schedules = { ...current.schedules };
          delete schedules[current.weekCommencing];
          return { schedules };
        },
        { reason: 'schedule-rule-refresh' },
      );
      actions.generate();
      actions.toast({
        message: 'Lunches refreshed to apply the latest timing, NISCC and confirmed staff rules.',
        tone: 'ok',
        duration: 7000,
      });
      return true;
    },

    /**
     * One-time recovery for the exact two-page benchmark PDF misread by the old
     * vertical row merger. The project already carries a reconciled transcript
     * of this rota; matching the week, source and complete employee cohort keeps
     * the repair narrow and prevents a manager's unrelated rota being touched.
     */
    recoverCorruptBenchmarkPdfIfNeeded() {
      const state = store.getState();
      const rota = state.rotas?.[state.weekCommencing] ?? null;
      if (!isKnownCorruptBenchmarkPdf(rota, state.weekCommencing)) return false;

      const repaired = normaliseRota({
        ...buildHistoricalRota(),
        source: 'recovered-pdf',
        importedAt: rota.importedAt ?? new Date().toISOString(),
      });
      store.setState(
        (current) => {
          const schedules = { ...current.schedules };
          delete schedules[current.weekCommencing];
          return {
            rotas: { ...current.rotas, [current.weekCommencing]: repaired },
            schedules,
          };
        },
        { reason: 'pdf-row-recovery' },
      );
      actions.generate();
      actions.toast({
        message: 'Recovered the rota table and rebuilt every day from all 32 staff.',
        tone: 'ok',
        duration: 8000,
      });
      return true;
    },

    regenerateDay(dayKey) {
      actions.generate({ days: [dayKey] });
    },

    moveAssignment(dayKey, employeeId, slotMinutes) {
      updateSchedule((schedule, state) => applyManualAssignment(schedule, {
        dayKey,
        employeeId,
        slotMinutes,
        deps: schedulerDeps(state),
      }));
    },

    addAssignment(dayKey, employeeId, slotMinutes) {
      actions.moveAssignment(dayKey, employeeId, slotMinutes);
    },

    removeAssignment(dayKey, employeeId) {
      updateSchedule((schedule, state) => removeAssignmentFromSchedule(
        schedule,
        dayKey,
        employeeId,
        schedulerDeps(state),
      ));
    },

    toggleLock(dayKey, employeeId, locked) {
      updateSchedule((schedule, state) => toggleLockInSchedule(
        schedule,
        dayKey,
        employeeId,
        locked,
        schedulerDeps(state),
      ));
    },

    acknowledgeWarning(warningId, reason) {
      updateSchedule((schedule) => acknowledgeWarningInSchedule(schedule, warningId, reason));
      actions.toast({ message: 'Warning overridden with a reason.', tone: 'info' });
    },

    /* ---------------------------------------------------------------- */
    /* Settings                                                          */
    /* ---------------------------------------------------------------- */

    updateSettings(patch) {
      store.setState(
        (state) => ({ settings: normaliseSettings({ ...state.settings, ...patch }) }),
        { reason: 'settings' },
      );
    },

    updateProfile(scope, patch) {
      store.setState(
        (state) => ({
          settings: normaliseSettings({ ...state.settings, [scope]: { ...state.settings[scope], ...patch } }),
        }),
        { reason: 'settings' },
      );
    },

    updateDayProfile(dayKey, patch) {
      store.setState(
        (state) => ({
          settings: normaliseSettings({
            ...state.settings,
            days: { ...state.settings.days, [dayKey]: { ...(state.settings.days?.[dayKey] ?? {}), ...patch } },
          }),
        }),
        { reason: 'settings' },
      );
    },

    clearDayProfile(dayKey) {
      store.setState(
        (state) => {
          const days = { ...state.settings.days };
          delete days[dayKey];
          return { settings: normaliseSettings({ ...state.settings, days }) };
        },
        { reason: 'settings' },
      );
    },

    updateScoringWeights(patch) {
      store.setState(
        (state) => ({
          settings: normaliseSettings({
            ...state.settings,
            scoringWeights: { ...state.settings.scoringWeights, ...patch },
          }),
        }),
        { reason: 'settings' },
      );
    },

    resetSettings() {
      store.setState({ settings: createDefaultSettings() }, { reason: 'settings' });
      actions.toast({ message: 'Scheduling settings restored to defaults.', tone: 'ok' });
    },

    /* ---------------------------------------------------------------- */
    /* Staff rules & aliases                                             */
    /* ---------------------------------------------------------------- */

    saveStaffRule(rule) {
      const normalised = createStaffRule(rule);
      if (!normalised.employeeId) {
        actions.toast({ message: 'Choose an employee for this rule.', tone: 'warn' });
        return;
      }
      store.setState(
        (state) => {
          const existing = state.staffRules.findIndex((item) => item.employeeId === normalised.employeeId);
          const staffRules = existing >= 0
            ? state.staffRules.map((item, index) => (index === existing ? normalised : item))
            : [...state.staffRules, normalised];
          return { staffRules };
        },
        { reason: 'rules' },
      );
      actions.toast({ message: `Saved rule for ${normalised.label}.`, tone: 'ok' });
    },

    deleteStaffRule(employeeId) {
      store.setState(
        (state) => ({ staffRules: state.staffRules.filter((rule) => rule.employeeId !== employeeId) }),
        { reason: 'rules' },
      );
    },

    restoreSeedStaffRules() {
      store.setState(
        (state) => {
          const seeds = buildSeedStaffRules();
          const byId = new Map(state.staffRules.map((rule) => [rule.employeeId, rule]));
          for (const seed of seeds) if (!byId.has(seed.employeeId)) byId.set(seed.employeeId, seed);
          return { staffRules: [...byId.values()] };
        },
        { reason: 'rules' },
      );
      actions.toast({ message: 'Built-in staff rules restored.', tone: 'ok' });
    },

    setAlias(employeeId, displayNameValue) {
      store.setState(
        (state) => {
          const entries = { ...state.aliasBook.entries };
          if (displayNameValue?.trim()) entries[employeeId] = displayNameValue.trim();
          else delete entries[employeeId];
          return { aliasBook: { ...state.aliasBook, entries } };
        },
        { reason: 'alias' },
      );
    },

    resolveAlias(token, employeeId) {
      store.setState(
        (state) => ({
          aliasBook: {
            ...state.aliasBook,
            unresolved: state.aliasBook.unresolved.map((entry) => (
              entry.token === token ? { ...entry, resolvedTo: employeeId || null } : entry
            )),
            entries: employeeId
              ? { ...state.aliasBook.entries, [employeeId]: token }
              : state.aliasBook.entries,
          },
        }),
        { reason: 'alias' },
      );
      actions.toast({
        message: employeeId ? `"${token}" mapped successfully.` : `"${token}" left unresolved.`,
        tone: employeeId ? 'ok' : 'info',
      });
    },

    dismissUnresolvedAlias(token) {
      store.setState(
        (state) => ({
          aliasBook: {
            ...state.aliasBook,
            unresolved: state.aliasBook.unresolved.filter((entry) => entry.token !== token),
          },
        }),
        { reason: 'alias' },
      );
    },

    restoreSeedAliases() {
      store.setState(
        (state) => {
          const seed = buildSeedAliasBook();
          return {
            aliasBook: {
              entries: { ...seed.entries, ...state.aliasBook.entries },
              unresolved: state.aliasBook.unresolved.length ? state.aliasBook.unresolved : seed.unresolved,
            },
          };
        },
        { reason: 'alias' },
      );
      actions.toast({ message: 'Built-in aliases restored.', tone: 'ok' });
    },

    /* ---------------------------------------------------------------- */
    /* Export preferences & reset                                        */
    /* ---------------------------------------------------------------- */

    setExportOptions(patch) {
      store.setState(
        (state) => ({ exportOptions: { ...state.exportOptions, ...patch } }),
        { reason: 'export' },
      );
    },

    resetEverything() {
      const fresh = resetAppState() ?? createDefaultAppState();
      store.setState(
        (state) => ({ ...fresh, runtime: { ...state.runtime, dialog: null, dialogPayload: null } }),
        { reason: 'reset' },
      );
      actions.setTheme(fresh.theme ?? 'system');
      actions.toast({ message: 'Lunchly has been reset to its defaults.', tone: 'ok' });
    },

    replaceState(nextState) {
      store.setState(
        (state) => ({ ...nextState, runtime: state.runtime }),
        { reason: 'restore' },
      );
      actions.setTheme(nextState.theme ?? 'system');
    },
  };

  return actions;
}

function emptyCells() {
  const cells = {};
  for (const dayKey of DAY_KEYS) cells[dayKey] = '';
  return cells;
}

function unreadableRotaCellCount(rota) {
  let count = 0;
  for (const row of rota?.rows ?? []) {
    for (const dayKey of DAY_KEYS) {
      const value = String(row.cells?.[dayKey] ?? '').trim();
      if (value && classifyShiftCell(value).kind === 'unparsed') count += 1;
    }
  }
  return count;
}

function isKnownCorruptBenchmarkPdf(rota, weekCommencing) {
  if (!rota || weekCommencing !== DEMO_WEEK_COMMENCING || rota.source !== 'pdf') return false;
  if (unreadableRotaCellCount(rota) < HISTORICAL_ROTA_ROWS.length) return false;

  const actual = (rota.rows ?? [])
    .map((row) => String(row.name ?? '').trim().toLocaleLowerCase('en-GB'))
    .filter(Boolean)
    .sort();
  const expected = HISTORICAL_ROTA_ROWS
    .map((row) => row.name.toLocaleLowerCase('en-GB'))
    .sort();
  return actual.length === expected.length
    && actual.every((name, index) => name === expected[index]);
}
